<template>
  <div class="marketing-page qr-manager">
    <MarketingToolHeader
      eyebrow="Marketing Werkzeug"
      title="QR-Code Generator"
      description="Erstelle druckfertige QR-Codes für Kampagnen, Studioflächen und digitale Inhalte."
      icon="mdi-qrcode-scan">
      <template v-slot:actions>
        <span class="ready-pill"><span class="ready-dot"></span>Direkt einsatzbereit</span>
      </template>
    </MarketingToolHeader>

    <v-expansion-panels v-model="walkInPanel" flat class="walk-in-shortcut">
      <v-expansion-panel class="walk-in-shortcut__panel">
        <v-expansion-panel-header class="walk-in-shortcut__header">
          <div class="walk-in-shortcut__summary">
            <span class="walk-in-shortcut__icon"><v-icon>mdi-walk</v-icon></span>
            <div>
              <div class="walk-in-shortcut__title"><span>Neu</span><strong>Walk-in QR-Code</strong></div>
              <p>Studio auswählen und den passenden Link samt QR-Code direkt erstellen.</p>
            </div>
          </div>
        </v-expansion-panel-header>

        <v-expansion-panel-content>
          <div class="walk-in-shortcut__content">
            <v-autocomplete
              v-model="walkInStudioCode"
              :items="walkInStudios"
              :loading="loadingWalkInStudios"
              :disabled="loadingWalkInStudios"
              item-text="name"
              item-value="code"
              label="Studio auswählen"
              no-data-text="Keine Studios gefunden"
              prepend-inner-icon="mdi-map-marker-outline"
              class="walk-in-studio-field"
              dark
              outlined
              clearable
              hide-details="auto" />

            <v-alert v-if="walkInStudiosError" dense outlined type="error" class="walk-in-studio-error">
              <div class="walk-in-studio-error__content">
                <span>Die Studios konnten nicht geladen werden.</span>
                <v-btn text small color="error" @click.stop="fetchWalkInStudios">Erneut versuchen</v-btn>
              </div>
            </v-alert>

            <v-expand-transition>
              <div v-if="walkInLink" class="walk-in-link-card" aria-live="polite">
                <div>
                  <span>Fertiger Walk-in-Link</span>
                  <a :href="walkInLink" target="_blank" rel="noopener noreferrer">{{ walkInLink }}</a>
                </div>
                <v-chip small outlined color="#58d388"><v-icon left small>mdi-check-circle-outline</v-icon>QR-Vorschau aktualisiert</v-chip>
              </div>
            </v-expand-transition>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <section class="qr-workspace">
      <v-card dark flat class="control-surface">
        <div class="surface-heading">
          <span class="step-mark">1</span>
          <div>
            <div class="surface-eyebrow">Inhalt</div>
            <h2>Link oder Text eingeben</h2>
            <p>Die Vorschau aktualisiert sich automatisch nach der Eingabe.</p>
          </div>
        </div>

        <v-textarea
          v-model="link"
          dark
          outlined
          auto-grow
          rows="3"
          label="Link oder Text für den QR-Code"
          prepend-inner-icon="mdi-link-variant"
          class="modern-field qr-input"
          hide-details="auto" />

        <div class="input-hint">
          <v-icon small>mdi-lightbulb-outline</v-icon>
          <span>Für Webseiten am besten immer die vollständige Adresse inklusive https:// verwenden.</span>
        </div>

        <div class="action-row">
          <v-btn text class="clear-action" :disabled="!link" @click="link = ''"><v-icon left>mdi-close-circle-outline</v-icon>Leeren</v-btn>
          <v-spacer />
          <v-btn color="#009640" depressed class="primary-action" :disabled="!hasContent" :loading="downloading" @click="downloadQRCode">
            <v-icon left>mdi-download</v-icon>QR-Code herunterladen
          </v-btn>
        </div>
      </v-card>

      <v-card dark flat class="preview-surface">
        <div class="surface-heading preview-heading">
          <span class="step-mark">2</span>
          <div>
            <div class="surface-eyebrow">Vorschau</div>
            <h2>QR-Code prüfen</h2>
            <p>Die helle Fläche entspricht dem exportierten Motiv.</p>
          </div>
        </div>

        <div class="qr-stage" :class="{ 'is-empty': !previewLink }">
          <v-img v-if="previewLink" :src="'/api/qrcode/image?data=' + encodedLink" width="100%" max-width="300" contain class="qr-image">
            <template v-slot:placeholder
              ><div class="qr-loader"><v-progress-circular indeterminate color="#009640" width="3" /></div
            ></template>
          </v-img>
          <div v-else class="qr-placeholder"><v-icon>mdi-qrcode</v-icon><span>Warte auf deine Eingabe</span></div>
        </div>

        <div class="preview-meta">
          <span><v-icon small>mdi-file-image-outline</v-icon>PNG Export</span>
          <span><v-icon small>mdi-printer-outline</v-icon>Druckgeeignet</span>
          <span><v-icon small>mdi-cellphone-check</v-icon>Mobil lesbar</span>
        </div>
      </v-card>
    </section>
  </div>
</template>

<script>
import api from "../../../api"
import MarketingToolHeader from "./MarketingToolHeader.vue"

export default {
  components: { MarketingToolHeader },
  data() {
    return {
      link: "https://app.wellcomefit.ch",
      previewLink: "https://app.wellcomefit.ch",
      previewTimer: null,
      downloading: false,
      walkInPanel: null,
      walkInStudios: [],
      walkInStudioCode: null,
      loadingWalkInStudios: false,
      walkInStudiosError: false,
    }
  },
  computed: {
    hasContent() {
      return Boolean((this.link || "").trim())
    },
    encodedLink() {
      return encodeURIComponent(this.previewLink)
    },
    walkInLink() {
      const code = String(this.walkInStudioCode || "").trim()
      return code ? `https://wellcomefit.ch/walk-in?studio=${encodeURIComponent(code)}` : ""
    },
  },
  watch: {
    link(value) {
      clearTimeout(this.previewTimer)
      this.previewTimer = setTimeout(() => {
        this.previewLink = (value || "").trim()
      }, 350)
    },
    walkInPanel(value) {
      if (value === 0 && !this.walkInStudios.length && !this.loadingWalkInStudios) this.fetchWalkInStudios()
    },
    walkInStudioCode(value) {
      if (!value) return
      this.link = this.walkInLink
      this.previewLink = this.walkInLink
    },
  },
  beforeDestroy() {
    clearTimeout(this.previewTimer)
  },
  methods: {
    async fetchWalkInStudios() {
      this.loadingWalkInStudios = true
      this.walkInStudiosError = false
      try {
        const studios = await api.fetchPublicStudios()
        this.walkInStudios = (Array.isArray(studios) ? studios : []).filter(
          (studio) => studio && studio.code && !["berg", "sennwald"].includes(String(studio.code).toLowerCase()),
        )
      } catch (error) {
        this.walkInStudiosError = true
      } finally {
        this.loadingWalkInStudios = false
      }
    },
    async downloadQRCode() {
      if (!this.hasContent) return
      this.downloading = true
      try {
        await api.fetchQRCode(this.link.trim())
      } finally {
        this.downloading = false
      }
    },
  },
}
</script>

<style scoped>
.marketing-page {
  --surface: rgba(255, 255, 255, 0.035);
  --border: rgba(255, 255, 255, 0.1);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  max-width: 92.5rem;
  margin: 0 auto;
  padding: 1.5rem;
  color: var(--text);
  font-size: 1rem;
}

.ready-pill,
.preview-meta span,
.input-hint {
  display: inline-flex;
  align-items: center;
}

.ready-pill {
  min-height: 2rem;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border: 1px solid rgba(0, 150, 64, 0.28);
  border-radius: 999px;
  color: #d4d4d8;
  background: rgba(0, 150, 64, 0.1);
  font-size: 0.75rem;
  font-weight: 750;
}

.ready-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #58d388;
  box-shadow: 0 0 0 0.25rem rgba(0, 150, 64, 0.12);
}

.walk-in-shortcut {
  margin-top: 1rem;
}

.walk-in-shortcut__panel {
  overflow: hidden;
  border: 1px solid rgba(88, 211, 136, 0.22);
  border-radius: 1.125rem !important;
  background: linear-gradient(135deg, rgba(0, 150, 64, 0.12), rgba(255, 255, 255, 0.035)) !important;
}

.walk-in-shortcut__header {
  min-height: 5rem;
  padding: 1rem 1.25rem;
}

.walk-in-shortcut__summary {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.875rem;
  padding-right: 0.75rem;
}

.walk-in-shortcut__icon {
  display: grid;
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 2.75rem;
  place-items: center;
  border: 1px solid rgba(88, 211, 136, 0.28);
  border-radius: 0.875rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.14);
}

.walk-in-shortcut__icon .v-icon {
  color: inherit;
}

.walk-in-shortcut__title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  color: var(--text);
  font-size: 1rem;
}

.walk-in-shortcut__title > span {
  min-height: 1.5rem;
  padding: 0.1875rem 0.5rem;
  border-radius: 999px;
  color: #b9f6ce;
  background: rgba(0, 150, 64, 0.18);
  font-size: 0.75rem;
  font-weight: 800;
  line-height: 1.125rem;
  text-transform: uppercase;
}

.walk-in-shortcut__summary p {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.45;
}

.walk-in-shortcut ::v-deep .v-expansion-panel-header__icon .v-icon {
  color: #d4d4d8;
}

.walk-in-shortcut ::v-deep .v-expansion-panel-content__wrap {
  padding: 0 1.25rem 1.25rem;
}

.walk-in-shortcut__content {
  padding-top: 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.walk-in-studio-field {
  max-width: 34rem;
}

.walk-in-studio-field ::v-deep .v-input__slot {
  min-height: 3.5rem;
  border-radius: 0.875rem !important;
  background: rgba(255, 255, 255, 0.035) !important;
}

.walk-in-studio-field ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.16) !important;
}

.walk-in-studio-field ::v-deep .v-label,
.walk-in-studio-field ::v-deep input {
  font-size: 1rem;
}

.walk-in-studio-error {
  max-width: 34rem;
  margin-top: 0.75rem;
  font-size: 0.875rem;
}

.walk-in-studio-error__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.walk-in-link-card {
  display: flex;
  min-width: 0;
  max-width: 50rem;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
  padding: 0.875rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.16);
}

.walk-in-link-card > div {
  min-width: 0;
}

.walk-in-link-card > div > span {
  display: block;
  margin-bottom: 0.375rem;
  color: var(--muted);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.walk-in-link-card a {
  display: block;
  color: #86efac;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.5;
  overflow-wrap: anywhere;
  text-decoration: none;
}

.walk-in-link-card a:hover,
.walk-in-link-card a:focus-visible {
  text-decoration: underline;
}

.qr-workspace {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(22rem, 0.82fr);
  gap: 1rem;
  margin-top: 1rem;
}

.control-surface,
.preview-surface {
  min-width: 0;
  padding: 1.5rem;
  border: 1px solid var(--border);
  border-radius: 1.375rem !important;
  background: var(--surface) !important;
  box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.12);
}

.surface-heading {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.step-mark {
  display: grid;
  flex: 0 0 2.5rem;
  width: 2.5rem;
  height: 2.5rem;
  place-items: center;
  border: 1px solid rgba(0, 150, 64, 0.26);
  border-radius: 0.75rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.11);
  font-size: 0.875rem;
  font-weight: 800;
}

.surface-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.surface-heading h2 {
  margin: 0.1875rem 0 0;
  color: var(--text);
  font-size: 1.25rem;
}

.surface-heading p {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.modern-field ::v-deep .v-input__slot {
  min-height: 7.5rem;
  border-radius: 0.875rem !important;
  background: rgba(255, 255, 255, 0.035) !important;
}

.modern-field ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.modern-field ::v-deep .v-label,
.modern-field ::v-deep textarea {
  font-size: 1rem;
}

.input-hint {
  gap: 0.5rem;
  margin-top: 0.875rem;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.input-hint .v-icon {
  color: #fcd34d;
}

.action-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--border);
}

.primary-action,
.clear-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.primary-action {
  color: #ffffff !important;
}

.clear-action {
  color: var(--text-soft) !important;
}

.preview-surface {
  display: flex;
  flex-direction: column;
}

.qr-stage {
  display: grid;
  min-height: 22rem;
  flex: 1;
  place-items: center;
  padding: 1.5rem;
  border-radius: 1.125rem;
  background: #ffffff;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.06), 0 0.75rem 2rem rgba(0, 0, 0, 0.18);
}

.qr-stage.is-empty {
  background: rgba(255, 255, 255, 0.045);
  box-shadow: inset 0 0 0 1px var(--border);
}

.qr-image {
  width: 100%;
}

.qr-loader {
  display: grid;
  min-height: 18.75rem;
  place-items: center;
}

.qr-placeholder {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 0.75rem;
  color: var(--muted);
  font-size: 0.875rem;
}

.qr-placeholder .v-icon {
  color: #77797f;
  font-size: 5rem;
}

.preview-meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
}

.preview-meta span {
  min-height: 1.75rem;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border: 1px solid var(--border);
  border-radius: 999px;
  color: var(--muted);
  background: rgba(255, 255, 255, 0.035);
  font-size: 0.75rem;
  font-weight: 700;
}

.preview-meta .v-icon {
  color: #8f9095;
}

@media (max-width: 59.9375rem) {
  .marketing-page {
    padding: 1.125rem;
  }

  .qr-workspace {
    grid-template-columns: minmax(0, 1fr);
  }

  .preview-surface {
    order: -1;
  }
}

@media (max-width: 37.4375rem) {
  .marketing-page {
    padding: 0.75rem;
  }

  .ready-pill {
    width: 100%;
    justify-content: center;
  }

  .walk-in-shortcut {
    margin-top: 0.75rem;
  }

  .walk-in-shortcut__header {
    padding: 1rem;
  }

  .walk-in-shortcut__summary {
    align-items: flex-start;
    padding-right: 0.375rem;
  }

  .walk-in-shortcut__icon {
    width: 2.5rem;
    height: 2.5rem;
    flex-basis: 2.5rem;
  }

  .walk-in-shortcut ::v-deep .v-expansion-panel-content__wrap {
    padding: 0 1rem 1rem;
  }

  .walk-in-link-card,
  .walk-in-studio-error__content {
    align-items: stretch;
    flex-direction: column;
  }

  .walk-in-link-card .v-chip {
    align-self: flex-start;
  }

  .qr-workspace {
    gap: 0.75rem;
    margin-top: 0.75rem;
  }

  .control-surface,
  .preview-surface {
    padding: 1rem;
    border-radius: 1.125rem !important;
  }

  .surface-heading {
    margin-bottom: 1rem;
  }

  .qr-stage {
    min-height: 18rem;
    padding: 1rem;
  }

  .action-row {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .action-row .v-btn {
    width: 100%;
  }
}
</style>
